import React from "react";
import { useTranslation } from "react-i18next";
import { Monitor, Smartphone, Tablet, CheckCircle } from "lucide-react";
import { useSecurity } from "../hooks/use-security";
import { formatDistanceToNow } from "date-fns";
import { enUS, tr, it } from "date-fns/locale";
import { LoginHistorySkeleton } from "@/components/ui/login-history-skeleton";

const getDeviceIcon = (type: string, isActive: boolean = false) => {
  const iconClass = isActive
    ? "w-5 h-5 text-green-600 dark:text-green-400"
    : "w-5 h-5 text-text-secondary dark:text-text-secondary";

  const icon = (() => {
    switch (type.toLowerCase()) {
      case "desktop":
        return <Monitor className={iconClass} />;
      case "mobile":
        return <Smartphone className={iconClass} />;
      case "tablet":
        return <Tablet className={iconClass} />;
      default:
        return <Monitor className={iconClass} />;
    }
  })();

  if (isActive) {
    return (
      <div className="relative">
        {icon}
        <CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400 absolute -top-1 -right-1 bg-white dark:bg-gray-900 rounded-full" />
      </div>
    );
  }

  return icon;
};

export const DeviceSection: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Security hook - handles all state and actions
  const {
    loginHistory,
    isLoginHistoryLoading,
    hasNextPage,
    isFetchingNextPage,
    handleLoadMore,
  } = useSecurity();

  // Get date-fns locale based on current language
  const getDateLocale = () => {
    switch (i18n.resolvedLanguage || i18n.language) {
      case "tr":
        return tr;
      case "it":
        return it;
      case "en":
      default:
        return enUS;
    }
  };

  // Format last active time
  const formatLastActive = (createdAt: string) => {
    try {
      const date = new Date(createdAt);
      const now = new Date();

      // Check if it's within the last hour (active now)
      const diffInMinutes = (now.getTime() - date.getTime()) / (1000 * 60);
      if (diffInMinutes < 60) {
        return t("settings.password.activeNow");
      }

      // Format using date-fns
      return formatDistanceToNow(date, {
        addSuffix: true,
        locale: getDateLocale(),
      });
    } catch {
      return createdAt;
    }
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      <div>
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-primary mb-2">
          {t("settings.password.whereLoggedIn")}
        </h3>
        <p className="text-sm text-text-tertiary dark:text-text-tertiary">
          {t("settings.password.whereLoggedInDescription")}
        </p>
      </div>

      <div className="flex-1 min-h-0">
        {isLoginHistoryLoading ? (
          <LoginHistorySkeleton count={5} />
        ) : loginHistory.length === 0 ? (
          <div className="flex items-center justify-center py-8 border border-border-secondary dark:border-border-secondary rounded-lg">
            <p className="text-sm text-text-tertiary dark:text-text-tertiary">
              {t("settings.security.noLoginHistory")}
            </p>
          </div>
        ) : (
          <div className="h-full max-h-[400px] overflow-y-auto border border-border-secondary dark:border-border-secondary rounded-lg">
            {loginHistory
              .filter((item) => item && item.createdAt) // Undefined item'ları filtrele
              .map((item, index) => {
                const lastActive = formatLastActive(item.createdAt);
                const isActive = index === 0; // İlk eleman her zaman active

                return (
                  <div
                    key={item._id}
                    className={`flex items-center gap-4 p-4 ${
                      isActive
                        ? "bg-green-50 dark:bg-green-900/20 border-l-4 border-l-green-500 dark:border-l-green-400"
                        : ""
                    } ${
                      index !== loginHistory.length - 1
                        ? "border-b border-border-secondary dark:border-border-secondary"
                        : ""
                    }`}
                  >
                    {/* Device Icon */}
                    <div className="flex-shrink-0">
                      {getDeviceIcon(item.type, isActive)}
                    </div>

                    {/* Device Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-text-primary dark:text-text-primary truncate">
                          {item.device}
                        </p>
                        {/* {isActive && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            {t("settings.security.currentSession")}
                          </span>
                        )} */}
                      </div>
                      <p className="text-xs text-text-tertiary dark:text-text-tertiary">
                        {item.location} • {lastActive}
                      </p>
                      <p className="text-xs text-text-tertiary dark:text-text-tertiary mt-1">
                        IP: {item.ipAddress}
                      </p>
                    </div>
                  </div>
                );
              })}

            {/* Load More Button */}
            {hasNextPage && (
              <div className="flex justify-center py-4 border-t border-border-secondary dark:border-border-secondary">
                <button
                  onClick={handleLoadMore}
                  disabled={isFetchingNextPage}
                  className="px-4 py-2 text-sm font-medium text-text-primary dark:text-text-primary bg-bg-primary dark:bg-bg-primary border border-border-secondary rounded-md hover:bg-bg-secondary dark:hover:bg-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isFetchingNextPage ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      {t("common.loading")}
                    </div>
                  ) : (
                    t("common.loadMore")
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
